import type { To } from 'react-router-dom';

import { LANDING_SITE_FOOTER_ID, LEGAL_RETURN_FROM_LANDING_FOOTER } from '@constants/legal';

export type LegalReturnFrom =
  | 'register'
  | 'login'
  | 'complete-invitation'
  | 'family-registration'
  | typeof LEGAL_RETURN_FROM_LANDING_FOOTER;

export interface LegalPageLocationState {
  from?: string;
  scrollTo?: string;
}

export interface LegalBackTarget {
  path: string;
  label: string;
  state?: LegalPageLocationState;
}

export interface LegalPageLinkOptions {
  returnFrom: LegalReturnFrom;
  /** Required when returnFrom is complete-invitation (preserves invite token on back). */
  invitationToken?: string;
}

/** Pass on Link from landing footer → Terms / Privacy (same-tab state). */
export function legalLinkStateFromLandingFooter(): LegalPageLocationState {
  return { from: LEGAL_RETURN_FROM_LANDING_FOOTER };
}

/** Resolve `from` from router state or URL (URL works in new tabs). */
export function resolveLegalReturnFrom(
  locationState: LegalPageLocationState | null | undefined,
  searchParams: URLSearchParams
): string | undefined {
  const fromState = locationState?.from?.trim();
  if (fromState) return fromState;
  const fromQuery = searchParams.get('from')?.trim();
  return fromQuery || undefined;
}

export function buildLegalPageTo(pathname: string, options: LegalPageLinkOptions): To {
  const params = new URLSearchParams({ from: options.returnFrom });
  if (options.returnFrom === 'complete-invitation' && options.invitationToken?.trim()) {
    params.set('token', options.invitationToken.trim());
  }
  return { pathname, search: `?${params.toString()}` };
}

/** Absolute path string for `<a href>` (new-tab legal links). */
export function buildLegalPageHref(pathname: string, options: LegalPageLinkOptions): string {
  const to = buildLegalPageTo(pathname, options);
  if (typeof to === 'string') return to;
  return `${to.pathname}${to.search ?? ''}`;
}

export interface ResolveLegalBackTargetOptions {
  /** Signed-in parent who still needs to finish this year's family registration. */
  pendingFamilyRegistration?: boolean;
}

export function resolveLegalBackTarget(
  locationState: LegalPageLocationState | null | undefined,
  searchParams: URLSearchParams,
  options?: ResolveLegalBackTargetOptions
): LegalBackTarget | undefined {
  const from = resolveLegalReturnFrom(locationState, searchParams);

  if (
    options?.pendingFamilyRegistration &&
    from &&
    from !== LEGAL_RETURN_FROM_LANDING_FOOTER &&
    from !== 'family-registration'
  ) {
    return { path: '/family-registration', label: 'Back to family registration' };
  }

  if (from === 'register') {
    return { path: '/register', label: 'Back to registration' };
  }
  if (from === 'family-registration') {
    return { path: '/family-registration', label: 'Back to family registration' };
  }
  if (from === 'login') {
    return { path: '/login', label: 'Back to log in' };
  }
  if (from === 'complete-invitation') {
    const token = searchParams.get('token')?.trim();
    const path = token
      ? `/complete-invitation?token=${encodeURIComponent(token)}`
      : '/login';
    return { path, label: 'Back to sign up' };
  }
  if (from === LEGAL_RETURN_FROM_LANDING_FOOTER) {
    return {
      path: '/',
      label: 'Back to home page',
      state: { scrollTo: LANDING_SITE_FOOTER_ID },
    };
  }
  return undefined;
}
